import { create } from "zustand";
import { api, ApiError } from "./api";

/** v0.9 §5 rolleri; yetki kontrolü her zaman backend'de yapılır, buradaki değer yalnızca menüleri şekillendirir. */
export type Role = "STORE_ADMIN" | "SALES_REP" | "TENANT_AUDITOR" | "PLATFORM_OPS" | "SUPPORT";

export interface Membership {
  tenantId: string;
  displayName: string;
  roles: readonly Role[];
}

/** `GET /api/console/me` */
export interface Me {
  email: string;
  platformOps: boolean;
  activeTenant: string | null;
  roles: readonly Role[];
  supportUntil: string | null;
  tenants: readonly Membership[];
}

export type LoginStep =
  | { kind: "password" }
  | { kind: "totp" }
  | { kind: "enroll"; secret: string; otpauthUri: string };

interface SessionState {
  me: Me | null;
  /** İlk `me` isteği tamamlandı mı (giriş ekranı yanıp sönmesin). */
  loaded: boolean;
  step: LoginStep;
  refresh(): Promise<void>;
  login(email: string, password: string): Promise<void>;
  verifyTotp(code: string): Promise<void>;
  selectTenant(tenantId: string): Promise<void>;
  openSupport(tenantId: string, reason: string): Promise<void>;
  logout(): Promise<void>;
  /** Testler için. */
  set(me: Me | null): void;
}

type LoginStatus = "AUTHENTICATED" | "TOTP_REQUIRED" | "TOTP_ENROLLMENT_REQUIRED" | "INVALID_CREDENTIALS";

export const useSession = create<SessionState>((set, get) => ({
  me: null,
  loaded: false,
  step: { kind: "password" },

  async refresh() {
    try {
      const me = await api.get<Me>("/api/console/me");
      set({ me, loaded: true, step: { kind: "password" } });
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        set({ me: null, loaded: true });
        return;
      }
      set({ loaded: true });
      throw error;
    }
  },

  async login(email, password) {
    const { status } = await api.post<{ status: LoginStatus }>("/api/console/auth/login", { email, password });
    if (status === "AUTHENTICATED") {
      await get().refresh();
    } else if (status === "TOTP_REQUIRED") {
      set({ step: { kind: "totp" } });
    } else if (status === "TOTP_ENROLLMENT_REQUIRED") {
      const enrollment = await api.post<{ secret: string; otpauthUri: string }>("/api/console/auth/totp/enrollment");
      set({ step: { kind: "enroll", ...enrollment } });
    }
  },

  async verifyTotp(code) {
    await api.post("/api/console/auth/totp", { code });
    await get().refresh();
  },

  async selectTenant(tenantId) {
    await api.put("/api/console/session/tenant", { tenantId });
    await get().refresh();
  },

  async openSupport(tenantId, reason) {
    await api.post("/api/console/support-sessions", { tenantId, reason });
    await get().refresh();
  },

  async logout() {
    try {
      await api.post("/api/console/auth/logout");
    } finally {
      set({ me: null, step: { kind: "password" } });
    }
  },

  set(me) {
    set({ me, loaded: true });
  },
}));

export function hasRole(me: Me | null, ...roles: Role[]): boolean {
  return !!me && roles.some((r) => me.roles.includes(r));
}

/** Değiştiren işlemleri gösterir mi: yalnızca mağaza yöneticisi, destek oturumu salt okunur. */
export function canManage(me: Me | null): boolean {
  return hasRole(me, "STORE_ADMIN") && !hasRole(me, "SUPPORT");
}
